// src/context/WishlistContext.jsx
import React, { createContext, useState, useContext, useEffect } from 'react';
import { useUser } from './UserContext';
import { wishlistService } from '../services/wishlistService';

const WishlistContext = createContext();

const getValidId = (item) => {
  if (!item) return '';
  const id = item.outletVariantId || item.variantId || item.id;
  return id ? String(id) : '';
};

export const WishlistProvider = ({ children }) => {
  const [wishlistItems, setWishlistItems] = useState([]);
  const { isLoggedIn, user } = useUser();

  useEffect(() => {
    const syncAndFetchWishlist = async () => {
      if (isLoggedIn() && user) {
        try {
          const localWishlist = JSON.parse(localStorage.getItem('svmart_wishlist_local') || '[]');
          if (localWishlist.length > 0) {
            for (const item of localWishlist) {
              const vId = getValidId(item);
              if (vId) {
                try { await wishlistService.addToWishlist(vId); } catch(e){}
              }
            }
            localStorage.removeItem('svmart_wishlist_local');
          }
          
          const data = await wishlistService.getWishlist();
          const normalizedData = (data || []).map(item => ({
              ...item.outletVariant, 
              wishListItemId: item.wishListItemId
          }));
          setWishlistItems(normalizedData);
        } catch (error) {
          console.error("Error syncing wishlist:", error);
        }
      } else {
        const localWishlist = JSON.parse(localStorage.getItem('svmart_wishlist_local') || '[]');
        setWishlistItems(localWishlist);
      }
    };
    syncAndFetchWishlist();
  }, [isLoggedIn, user]);

  useEffect(() => {
    if (!isLoggedIn()) {
      localStorage.setItem('svmart_wishlist_local', JSON.stringify(wishlistItems));
    }
  }, [wishlistItems, isLoggedIn]);

  const isInWishlist = (product) => {
     const strId = typeof product === 'object' ? getValidId(product) : String(product);
     return wishlistItems.some(item => getValidId(item) === strId);
  };

  const addToWishlist = async (product) => {
    const vId = getValidId(product);
    if (isLoggedIn()) {
      try {
        await wishlistService.addToWishlist(vId);
        const data = await wishlistService.getWishlist();
        const normalizedData = (data || []).map(item => ({ ...item.outletVariant, wishListItemId: item.wishListItemId }));
        setWishlistItems(normalizedData);
      } catch(e) { console.error(e); }
    } else {
      setWishlistItems(prev => {
        if (prev.find(item => getValidId(item) === vId)) return prev;
        return [...prev, { ...product, outletVariantId: vId }];
      });
    }
  };

  const removeFromWishlist = async (productOrId) => {
    const vId = typeof productOrId === 'object' ? getValidId(productOrId) : String(productOrId);
    if (isLoggedIn()) {
      try {
        await wishlistService.removeFromWishlist(vId);
        const data = await wishlistService.getWishlist();
        const normalizedData = (data || []).map(item => ({ ...item.outletVariant, wishListItemId: item.wishListItemId }));
        setWishlistItems(normalizedData);
      } catch(e) { console.error(e); }
    } else {
      setWishlistItems(prev => prev.filter(item => getValidId(item) !== vId));
    }
  };

  const toggleWishlist = (product) => {
    const vId = getValidId(product);
    if (isInWishlist(vId)) {
      removeFromWishlist(vId);
    } else {
      addToWishlist(product);
    }
  };

  const getWishlistCount = () => wishlistItems.length;

  const value = {
    wishlistItems,
    addToWishlist,
    removeFromWishlist,
    isInWishlist,
    getWishlistCount,
    toggleWishlist
  };

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within WishlistProvider');
  }
  return context;
};